// ============================================================
// CTA DESIGN SYSTEM — single source of truth for course CTAs
// ============================================================
// Every Apply / Enroll button on a Course Visual Page is one atom
// (`dynamic-cta` in the editor registry) that reads everything about how it
// looks and where it points from THIS module. Nothing here is editable in
// the page Inspector — that is the point: the buttons are consistent
// site-wide and change in exactly one place.
//
// Four things live here, matching the CTA architecture spec:
//
//   1. Base atom styling  — CTA_BASE_STYLE: shape, size, borders, typography
//                           shared by every variant.
//   2. Fixed per-variant  — CTA_VARIANTS[v].bg / .hover: background and hover
//                           colours hardcoded per design system, Apply vs
//                           Enroll, NOT admin-editable.
//   3. Global text binding — CTA_TEXT: the label for each variant. This is
//                           the "single global source"; editing a value here
//                           updates that CTA everywhere it appears. (Interim
//                           implementation — see GLOBAL TEXT note below.)
//   4. Dynamic SKU URLs   — ctaHref(variant, sku): builds the target link
//                           from the course SKU, so links auto-generate and
//                           never need manual entry.
//
// GLOBAL TEXT note: these labels are now edited at runtime on the admin "Global
// Course Content" page and persisted in the Yii2 `global_course_content` table.
// The live values are served through `globalContentStore` (keys
// `cta_apply_label` / `cta_enroll_label`); `resolveCtaText()` reads them there.
// The CTA_TEXT constant below remains the FALLBACK used until hydration lands or
// when the backend is unavailable. Editing a label on the admin page updates
// that CTA everywhere it appears, with no change to any course page's JSON.

import { getGlobalValue } from './globalContentStore';

// ---- 2. Fixed per-variant design tokens --------------------------------
// bg/hover are hardcoded here and never surfaced as Inspector fields, so a
// page author cannot recolour a CTA off-brand.
export const CTA_VARIANTS = {
  apply: {
    label: 'Apply',
    // Gradient red per the visual reference. `bg` feeds the CSS `background`
    // shorthand in the atom's render, so a gradient string works as-is.
    bg: 'linear-gradient(180deg, #ff0000 0%, #cc0000 100%)',
    hover: 'linear-gradient(180deg, #e60000 0%, #a80000 100%)',
    text: '#FFFFFF',
  },
  enroll: {
    label: 'Enroll',
    bg: '#003C7D',       // SMWW dark navy blue
    hover: '#002A58',
    text: '#FFFFFF',
  },
};

export const CTA_VARIANT_KEYS = Object.keys(CTA_VARIANTS); // ['apply','enroll']

export const isCtaVariant = (v) => Object.prototype.hasOwnProperty.call(CTA_VARIANTS, v);

// ---- 1. Base atom styling ----------------------------------------------
// Shape / size / border / typography shared by every CTA regardless of
// variant. Per-variant background + text colour come from CTA_VARIANTS.
//
// SIZE SOURCE OF TRUTH: these box values now live in CSS as
// `.page-editor-root .l-cta .l-cta-a` (PageEditor.css) so every CTA is sized
// identically in View, Edit and newly added blocks without relying on inline
// styles. The constant below is kept as the documented reference for those
// values — keep the two in sync if either ever changes.
export const CTA_BASE_STYLE = {
  display: 'inline-block',
  padding: '13px 30px',
  borderRadius: '24px',        // full pill shape per the visual reference
  border: '0',
  fontWeight: 700,
  fontSize: '13px',
  letterSpacing: '0.6px',
  textTransform: 'uppercase',
  textDecoration: 'none',
  lineHeight: 1.2,
  textAlign: 'center',
  cursor: 'pointer',
  boxShadow: '0 3px 6px rgba(0,0,0,.18)',  // subtle drop shadow
};

// ---- 3. Global text binding --------------------------------------------
// Fallback CTA label text, one value per variant. The runtime source of truth
// is `globalContentStore` (keys `cta_apply_label` / `cta_enroll_label`); these
// mirror those fields' registered fallbacks.
export const CTA_TEXT = {
  apply: 'Apply Now',
  enroll: 'Enroll Now',
};

// The ONLY reader of the global CTA label text. Reads the live global value
// (with a compile-time fallback), so every CTA picks up an admin edit with no
// other changes.
export function resolveCtaText(variant) {
  const key = variant === 'enroll' ? 'cta_enroll_label' : 'cta_apply_label';
  return getGlobalValue(key);
}

// ---- 4. Dynamic SKU URLs -----------------------------------------------
// Build a CTA target from the course SKU (field_application_key_name).
//   apply  -> /application?course={SKU}#step1
//   enroll -> /smww_course_pay?lineitems={SKU}&quantity=1
// When no SKU is known yet (page not bound to a course, or the source page
// hasn't loaded) the base path is returned without a query, so the link is
// never broken — it just isn't course-specific until a SKU resolves.
export function ctaHref(variant, sku) {
  const s = sku ? encodeURIComponent(String(sku).trim()) : '';
  if (variant === 'enroll') {
    return s ? `/smww_course_pay?lineitems=${s}&quantity=1` : '/smww_course_pay';
  }
  // default + 'apply'
  return s ? `/application?course=${s}#step1` : '/application';
}

// Convenience for renderers: resolve the full presentation of a CTA in one
// call. `sku` is optional; omit until it resolves from the course record.
export function resolveCta(variant, sku) {
  const v = isCtaVariant(variant) ? variant : 'apply';
  const tokens = CTA_VARIANTS[v];
  return {
    variant: v,
    text: resolveCtaText(v),
    href: ctaHref(v, sku),
    bg: tokens.bg,
    hover: tokens.hover,
    color: tokens.text,
  };
}
